import {StageManager} from "../StageMaganer/StageManager";
import {PublicTicketsFormDefs} from "../PublicTicketsForm";

class GlobalState {
    /** @var {PublicTicketsFormDefs.Stage[]} */
    stageArray;
    /** @var {StageManager} */
    stageManager;
    /** @var {PublicTicketsFormDefs.Stage} */
    currentStage;

    /** @var {PublicEventResource.PublicEventResource|undefined} */
    parentEvent;

    /** @var {PublicEventResource.PublicEventResource|undefined} */
    selectedDate

    /** @var {PublicEventResource.PublicEventResource[]} */
    selectedDates

    /** @var {PublicEventResource.SeatType} */
    selectedSeatType

    /** @var {PublicTicketsFormDefs.ReservedSeatSelection[]} */
    selectedSeats

    /** @var {PublicTicketsFormDefs.SectionTicketCount[]} */
    generalAdmissionTicketCounts
    /** @var {PublicTicketsFormDefs.SectionTicketCount[]} */
    reserveTicketCounts

    /** @var {string} */
    discountCode

    /** @var {PublicTicketsFormDefs.Addon[]} */
    addons

    /** @var {PublicTicketsFormDefs.PaymentInfo} */
    paymentInfo

    /** @var {string} */
    howDidYouFindUs

    /** @var {boolean} */
    isCurrentEventView;

    /** @var {PublicTicketsFormDefs.Stage[]} */
    hiddenStages;

    /** @var {string} */
    baseRoute;

    /** @var {string} */
    adTrackingKeyword;

    /** @var {string} */
    adTrackingSessionPublicId;

    /** @var {boolean} */
    isUsingStripePaymentElements;

    /** @var {boolean} */
    recoveringFromCapacityConflict;

    /** @var {PublicTicketsFormDefs.PublicTicketConfig} */
    publicTicketConfig;


    constructor() {
        // Initial state
        this.stageArray = [
            'show-select',
            'date-select',
            'seat-select',
            'seat-type-select',
            'payment-info',
            'summary',
        ];

        this.selectedDate = undefined;
        this.selectedDates = [];
        this.selectedSeatType = undefined;
        this.selectedSeats = [];
        this.generalAdmissionTicketCounts = [];
        this.reserveTicketCounts = [];
        this.discountCode = '';
        this.addons = [];

        this.howDidYouFindUs = '';

        this.currentStage = 'show-select';

        this.isCurrentEventView = false;

        this.hiddenStages = [];

        this.baseRoute = '';

        this.adTrackingKeyword = '';
        this.adTrackingSessionPublicId = '';

        this.isUsingStripePaymentElements = false;
        this.recoveringFromCapacityConflict = false;
        this.stripeInstance = undefined;
        this.publicTicketConfig = {
            multimapTutorialShow: false,
            rvStallUpsell: this.getDisabledRvStallUpsellConfig(),
            forcedMinimumAddon: this.getDisabledForcedMinimumAddonConfig(),
        };

        // Stage Manager
        this.stageManager = this.getInitialStageManager();
    }

    isExpressMode() {
        // Extended by child class
        return false;
    }

    getInitialStageManager() {
        return new StageManager(this);
    }

    hasRecordChanged(originalRecord, newRecord, fieldToCheck = 'id') {
        // If either of them is null but the other isn't, we know it has changed
        if (!originalRecord && newRecord) {
            return true;
        }
        if (originalRecord && !newRecord) {
            return true;
        }

        // If both records are equal, then either both are empty and nothing changed, or they are
        // strict match which means neither has changed too.
        if (originalRecord === newRecord) {
            return false;
        }

        // Finally, both must be defined and not a strict match, compare by given field
        return !(originalRecord[fieldToCheck] === newRecord[fieldToCheck]);
    }

    /**
     * @param {PublicEventResource.PublicEventResource} parentEvent
     */
    setParentEvent(parentEvent) {
        // Check if we selected a different parent event
        const recordChanged = this.hasRecordChanged(
            this.parentEvent,
            parentEvent
        );

        // Update in memory
        this.parentEvent = parentEvent

        // Clear next stage if required
        if (recordChanged) {
            this.stageManager.clearStageInstance('date-select');

            this.selectedDate = undefined;
            this.selectedDates = [];
            this.clearStateAfterDateSelectionChange();
        }
    };


    /**
     * @param {PublicEventResource.PublicEventResource} eventDate
     */
    setEventDate(eventDate) {
        const selectedDates = eventDate
            ? [eventDate]
            : [];

        this.setSelectedDates(selectedDates);
    }

    /**
     * @param {PublicEventResource.PublicEventResource[]} eventDates
     */
    setSelectedDates(eventDates) {
        const normalizedDates = this.normalizeSelectedDates(eventDates);

        // Check if we selected a different event date
        const recordChanged = this.hasSelectedDateSetChanged(normalizedDates);

        // Update in memory
        this.selectedDates = normalizedDates;
        this.selectedDate = normalizedDates.length
            ? normalizedDates[0]
            : undefined;

        // Clear next stage if required
        if (recordChanged) {
            this.clearStateAfterDateSelectionChange();
        }
    }

    /**
     * @param {PublicEventResource.PublicEventResource[]} eventDates
     * @return {PublicEventResource.PublicEventResource[]}
     */
    normalizeSelectedDates(eventDates) {
        const uniqueDatesById = new Map();

        if (Array.isArray(eventDates) === false) {
            return [];
        }

        eventDates.forEach(eventDate => {
            if (eventDate && eventDate.id && uniqueDatesById.has(eventDate.id) === false) {
                uniqueDatesById.set(eventDate.id, eventDate);
            }
        });

        return Array.from(uniqueDatesById.values()).sort((firstDate, secondDate) => {
            const firstSortValue = this.getEventDateSortValue(firstDate);
            const secondSortValue = this.getEventDateSortValue(secondDate);

            if (firstSortValue !== secondSortValue) {
                return firstSortValue - secondSortValue;
            }

            return String(firstDate.id).localeCompare(String(secondDate.id));
        });
    }

    /**
     * @param {PublicEventResource.PublicEventResource} eventDate
     * @return {number}
     */
    getEventDateSortValue(eventDate) {
        const parsedHtmlDate = this.parseHtmlDateAndTime(eventDate?.htmlDate, eventDate?.timeOnly);

        if (parsedHtmlDate !== null) {
            return parsedHtmlDate;
        }

        const parsedDate = Date.parse(eventDate?.dateAndTime || '');

        return Number.isNaN(parsedDate)
            ? Number.MAX_SAFE_INTEGER
            : parsedDate;
    }

    /**
     * @param {string|null|undefined} htmlDate
     * @param {string|null|undefined} timeOnly
     * @return {number|null}
     */
    parseHtmlDateAndTime(htmlDate, timeOnly) {
        const dateMatch = String(htmlDate || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
        const timeMatch = String(timeOnly || '').match(/^(\d{1,2}):(\d{2})\s?(AM|PM)$/i);

        if (!dateMatch || !timeMatch) {
            return null;
        }

        const year = Number(dateMatch[1]);
        const monthIndex = Number(dateMatch[2]) - 1;
        const day = Number(dateMatch[3]);
        let hours = Number(timeMatch[1]);
        const minutes = Number(timeMatch[2]);
        const meridiem = timeMatch[3].toUpperCase();

        if (meridiem === 'PM' && hours < 12) {
            hours += 12;
        }

        if (meridiem === 'AM' && hours === 12) {
            hours = 0;
        }

        return new Date(year, monthIndex, day, hours, minutes).getTime();
    }

    /**
     * @param {PublicEventResource.PublicEventResource[]} nextSelectedDates
     * @return {boolean}
     */
    hasSelectedDateSetChanged(nextSelectedDates) {
        if (this.selectedDates.length !== nextSelectedDates.length) {
            return true;
        }

        return this.selectedDates.some((selectedDate, index) => {
            return selectedDate.id !== nextSelectedDates[index].id;
        });
    }

    clearStateAfterDateSelectionChange() {
        this.selectedSeatType = undefined;
        this.selectedSeats = [];
        this.generalAdmissionTicketCounts = [];
        this.reserveTicketCounts = [];
        this.addons = [];
        this.paymentInfo = undefined;

        this.stageManager.clearStageInstance('seat-select');
        this.stageManager.clearStageInstance('seat-type-select');
        this.stageManager.clearStageInstance('payment-info');
        this.stageManager.clearStageInstance('summary');
    }

    isMultipleDatesSelectionMode() {
        return this.parentEvent?.dateSelectionType === 'multiple_dates';
    }

    /**
     * @return {PublicEventResource.PublicEventResource[]}
     */
    getSelectedChildDates() {
        if (!this.selectedDate) {
            return [];
        }

        return this.selectedDates.filter(eventDate => eventDate.id !== this.selectedDate.id);
    }

    getSubOrderPayload() {
        return this.getSelectedChildDates().map(eventDate => {
            return {
                event_date_id: eventDate.id,
            };
        });
    }

    /**
     * @param {PublicEventResource.SeatType} seatType
     */
    setSeatType(seatType) {
        // Check if we selected a different seat type
        const recordChanged = this.hasRecordChanged(
            this.selectedSeatType,
            seatType
        );

        // Update in memory
        // noinspection JSValidateTypes
        this.selectedSeatType = seatType;

        // Clear next stage if required
        if (recordChanged) {
            // Clear next stage if seat was changed
            this.stageManager.clearStageInstance('seat-select');
            this.stageManager.clearStageInstance('seat-type-select');
            this.stageManager.clearStageInstance('summary');
        }
    }

    /**
     * Save the order id generated from the server
     *
     * @param {string} orderId
     */
    setOrderId(orderId) {
        this.orderId = orderId;
    }

    /**
     * @param {PublicTicketsFormDefs.ReservedSeatSelection[]} seats
     */
    setSelectedSeats(seats) {
        this.selectedSeats = seats;

        // TODO: check if seats were truly changed

        this.stageManager.clearStageInstance('seat-type-select');
    }

    /**
     * @param {PublicTicketsFormDefs.SectionTicketCount[]} tickets
     */
    setGeneralAdmissionTicketCounts(tickets) {
        this.generalAdmissionTicketCounts = tickets;

        // TODO: Check if ticket counts were truly changed

        this.stageManager.clearStageInstance('payment-info');
        this.stageManager.clearStageInstance('summary');
    }

    /**
     * @param {PublicTicketsFormDefs.SectionTicketCount[]} tickets
     */
    setReserveTicketCounts(tickets) {
        this.reserveTicketCounts = tickets;

        // TODO: Check if ticket counts were truly changed

        this.stageManager.clearStageInstance('payment-info');
        this.stageManager.clearStageInstance('summary');
    }

    /**
     * @param {PublicTicketsFormDefs.Addon[]} addons
     */
    setAddons(addons) {
        this.addons = addons;

        // TODO: Check if addons were truly changed

        this.stageManager.clearStageInstance('payment-info');
        this.stageManager.clearStageInstance('summary');
    }

    /**
     * @param {string} discountCode
     */
    setDiscountCode(discountCode) {
        this.discountCode = discountCode;

        // TODO: Check if discount code was truly changed

        // Clear payment and summary stage, unless we are in payment info stage
        // then clear only summary
        if (this.stageManager.globalState.currentStage !== 'payment-info') {
            this.stageManager.clearStageInstance('payment-info');
        }
        this.stageManager.clearStageInstance('summary');
    }

    /**
     * @param {PublicTicketsFormDefs.PaymentInfo} paymentInfo
     */
    setPaymentInfo(paymentInfo) {
        this.paymentInfo = paymentInfo;

        // TODO: Check if payment info was truly changed

        this.stageManager.clearStageInstance('summary');
    }

    /**
     * @param {string} howDidYouFindUs
     */
    setHowDidYouFindUs(howDidYouFindUs) {
        this.howDidYouFindUs = howDidYouFindUs;
    }

    /**
     * @param {boolean} isCurrentEventView
     */
    setIsCurrentEventView(isCurrentEventView) {
        this.isCurrentEventView = isCurrentEventView;
    }

    /**
     * @param {PublicTicketsFormDefs.Stage[]} hiddenStages
     */
    setHiddenStages(hiddenStages) {
        const normalizedHiddenStages = Array.isArray(hiddenStages)
            ? hiddenStages
            : [];

        this.hiddenStages = normalizedHiddenStages.filter(stage => this.stageArray.includes(stage));
    }

    /**
     * @param {Partial<PublicTicketsFormDefs.PublicTicketConfig>} config
     */
    setPublicTicketConfig(config) {
        this.publicTicketConfig = {
            multimapTutorialShow: config?.multimapTutorialShow === true,
            rvStallUpsell: this.normalizeRvStallUpsellConfig(config?.rvStallUpsell),
            forcedMinimumAddon: this.normalizeForcedMinimumAddonConfig(config?.forcedMinimumAddon),
        };
    }

    getDisabledForcedMinimumAddonConfig() {
        return {
            key: '',
        };
    }

    normalizeForcedMinimumAddonConfig(config) {
        if (!config || typeof config !== 'object') {
            return this.getDisabledForcedMinimumAddonConfig();
        }

        return {
            key: this.normalizePublicIdConfigValue(config.key),
        };
    }

    getDisabledRvStallUpsellConfig() {
        return {
            enabled: false,
            parentMultiMapPublicId: '',
            rvChildMapPublicId: '',
            stallsChildMapPublicId: '',
        };
    }

    normalizeRvStallUpsellConfig(config) {
        if (!config || typeof config !== 'object') {
            return this.getDisabledRvStallUpsellConfig();
        }

        const parentMultiMapPublicId = this.normalizePublicIdConfigValue(config.parentMultiMapPublicId);
        const rvChildMapPublicId = this.normalizePublicIdConfigValue(config.rvChildMapPublicId);
        const stallsChildMapPublicId = this.normalizePublicIdConfigValue(config.stallsChildMapPublicId);

        if (config.enabled !== true || !parentMultiMapPublicId || !rvChildMapPublicId || !stallsChildMapPublicId) {
            return this.getDisabledRvStallUpsellConfig();
        }

        return {
            enabled: true,
            parentMultiMapPublicId,
            rvChildMapPublicId,
            stallsChildMapPublicId,
        };
    }

    normalizePublicIdConfigValue(value) {
        return typeof value === 'string'
            ? value.trim()
            : '';
    }

    /**
     * @param {string} adTrackingKeyword
     * @param {string} adTrackingSessionPublicId
     */
    setAdTrackingContext(adTrackingKeyword, adTrackingSessionPublicId) {
        this.adTrackingKeyword = String(adTrackingKeyword || '').trim().toLowerCase();
        this.adTrackingSessionPublicId = String(adTrackingSessionPublicId || '').trim();

        // Keyword is authoritative for linking with orders.
        if (!this.adTrackingKeyword) {
            this.adTrackingSessionPublicId = '';
        }
    }

    /**
     * @param {string} trackingCode
     */
    setManualTrackingCode(trackingCode) {
        this.adTrackingKeyword = String(trackingCode || '').trim().toLowerCase();
        this.adTrackingSessionPublicId = '';
    }

    /**
     * @param {PublicTicketsFormDefs.Stage} stage
     */
    isStageHidden(stage) {
        return this.hiddenStages.includes(stage);
    }

    /**
     * @param {boolean} isUsingStripePaymentElements
     */
    setIsUsingStripePaymentElements(isUsingStripePaymentElements) {
        this.isUsingStripePaymentElements = isUsingStripePaymentElements;
    }

    setStripeInstance(stripeInstance) {
        this.stripeInstance = stripeInstance;
    }

    /**
     * Returns order data in a format the server can understand
     */
    getOrderDataForServer(useRealPaymentData = false) {
        // Build reserved seats array
        let reserved_seats = undefined;

        if (this.selectedSeats.length) {
            reserved_seats = this.selectedSeats;
        }


        // Build seats array
        const seats = [];

        this.generalAdmissionTicketCounts.forEach(seatCount => {
            seats.push({
                seat_group_id: seatCount.groupId,
                visitor_type: seatCount.visitorTypeId,
                quantity: seatCount.count,
            });
        })
        this.reserveTicketCounts.forEach(seatCount => {
            seats.push({
                seat_group_id: seatCount.groupId,
                visitor_type: seatCount.visitorTypeId,
                quantity: seatCount.count,
            });
        })

        // Build addons array
        const addons = this.addons.map(addon => {
            return {
                id: addon.id,
                key: addon.key,
                quantity: (addon.count === undefined) ? 1 : addon.count,
                frozen: addon.frozen,
            };
        })

        let paymentInfo = this.paymentInfo;

        // Set confirmation token
        if (paymentInfo && paymentInfo.stripe_confirmation_token) {
            paymentInfo.confirmation_token = paymentInfo.stripe_confirmation_token;
        }

        const discountsArray = this.discountCode ? [{code: this.discountCode}] : [];

        const data = {
            order_id: this.orderId,
            event_id: this.parentEvent.id,
            event_date_id: this.selectedDate.id,
            sub_orders: this.getSubOrderPayload(),

            seats,
            addons,
            reserved_seats,

            ...paymentInfo,
            how_did_you_find_us: this.howDidYouFindUs,
            discounts: discountsArray,
        };

        if (this.adTrackingKeyword) {
            data.ad_tracking_keyword = this.adTrackingKeyword;
            data.ad_tracking_session_public_id = this.adTrackingSessionPublicId;
        }

        return data;
    }
}

export {GlobalState};
